﻿// V4 — Mobile-first Members. Same data + write paths as Members.jsx,
// rendered as a clean searchable card list. Each member is a tap-
// friendly card with avatar circle, role/team chips, and an action
// row. Add/Edit form is reused from the desktop view through a
// render-prop slot.

import React, { useMemo, useState } from "react";
import {
  UserPlus, Pencil, Trash2, Search, BarChart3, Crown, Shield,
  Mail, Phone, X, Gavel, User as UserIcon, ShieldCheck,
  Award, DollarSign, Trophy, Volleyball, Users as UsersIcon,
  BadgeCheck
} from "lucide-react";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import PlayerProfile from "../components/PlayerProfile";
import Avatar from "../components/Avatar";
import MemberCard from "../components/MemberCard";
import { api } from "../api";
import { canDo } from "../data";

const ROLE_LABEL = {
  PRESIDENT: "President", VICE_PRESIDENT: "VP",
  FINANCE_HEAD: "Finance", CRICKET_HEAD: "Cricket Head",
  VOLLEYBALL_HEAD: "Volleyball Head", CAPTAIN: "Captain",
  PLAYER: "Player", UMPIRE: "Umpire", ADMIN: "Admin"
};
// Icon per role for the coloured chips (matches the reference card
// where each role tag carries its own glyph as well as its accent).
const ROLE_ICON = {
  PRESIDENT: Crown,
  VICE_PRESIDENT: Award,
  FINANCE_HEAD: DollarSign,
  CRICKET_HEAD: Trophy,
  VOLLEYBALL_HEAD: Volleyball,
  CAPTAIN: Award,
  PLAYER: UserIcon,
  UMPIRE: Gavel,
  ADMIN: ShieldCheck
};
const ROLE_PRIORITY = {
  ADMIN: 0, PRESIDENT: 1, VICE_PRESIDENT: 2,
  CAPTAIN: 3, CRICKET_HEAD: 4, VOLLEYBALL_HEAD: 4,
  FINANCE_HEAD: 5, PLAYER: 9
};
function topRole(roles) {
  const filtered = (roles || []).filter(r => r !== "MEMBER");
  if (!filtered.length) return "PLAYER";
  return filtered.sort((a, b) => (ROLE_PRIORITY[a] ?? 99) - (ROLE_PRIORITY[b] ?? 99))[0];
}
function avatarText(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const FILTERS = [
  { key: "all",       label: "All",        match: () => true },
  { key: "active",    label: "Active",     match: u => u.active },
  { key: "leaders",   label: "Leaders",    match: u => (u.roles || []).some(r => ["PRESIDENT","VICE_PRESIDENT","CAPTAIN","CRICKET_HEAD","VOLLEYBALL_HEAD","FINANCE_HEAD","ADMIN"].includes(r)) },
  { key: "umpires",   label: "Umpires",    match: u => (u.roles || []).includes("UMPIRE") },
  { key: "inactive",  label: "Inactive",   match: u => !u.active }
];

export default function MembersMobile({
  state, reload, user,
  openCreate, openEdit
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [profileFor, setProfileFor] = useState(null);

  const canCreate = canDo(user, "members", "create");
  const canUpdate = canDo(user, "members", "update");
  const canDelete = canDo(user, "members", "delete");

  const members = useMemo(() => {
    let pool = state.members;
    if (!isAllTeams) {
      const ids = new Set(activeTeam?.players || []);
      pool = pool.filter(m => ids.has(m.id));
    }
    const filterFn = FILTERS.find(f => f.key === filter)?.match || (() => true);
    const q = search.trim().toLowerCase();
    return pool
      .filter(filterFn)
      .filter(m => {
        if (!q) return true;
        return (
          (m.name  || "").toLowerCase().includes(q) ||
          (m.email || "").toLowerCase().includes(q) ||
          (m.phone || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  }, [state.members, activeTeam, isAllTeams, filter, search]);

  async function remove(m) {
    if (!(await confirm(`Deactivate member "${m.name}"? They can be reactivated from the database.`))) return;
    try {
      await api.deleteMember(m.id);
      await reload();
      toast.success("Member deactivated.");
    } catch (err) { toast.error(err.message || "Couldn't deactivate."); }
  }

  // Count by filter for chip badges
  const counts = useMemo(() => {
    const c = {};
    for (const f of FILTERS) {
      c[f.key] = state.members.filter(m => {
        if (!isAllTeams) {
          const ids = new Set(activeTeam?.players || []);
          if (!ids.has(m.id)) return false;
        }
        return f.match(m);
      }).length;
    }
    return c;
  }, [state.members, activeTeam, isAllTeams]);

  return (
    <div className="m-mem">
      <header className="m-mem-head">
        <div>
          <small>{isAllTeams ? "All Teams" : (activeTeam?.name || "Team")}</small>
          <h1>Members</h1>
        </div>
        {canCreate && (
          <button type="button" className="btn m-mem-addBtn" onClick={openCreate}>
            <UserPlus size={16}/> Add
          </button>
        )}
      </header>

      {/* Search */}
      <div className="m-mem-search">
        <Search size={16}/>
        <input
          placeholder="Search by name, email, phone…" aria-label="Search by name, email, phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Clear">
            <X size={16}/>
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="m-mem-filterRow">
        {FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            className={filter === f.key ? "m-mem-chip m-mem-chip--active" : "m-mem-chip"}
            onClick={() => setFilter(f.key)}
          >
            {f.label} <em>{counts[f.key]}</em>
          </button>
        ))}
      </div>

      {/* Result count */}
      <div className="m-mem-count">
        {members.length} {members.length === 1 ? "member" : "members"}
      </div>

      {/* Member cards */}
      {members.length === 0 ? (
        <div className="m-mem-empty">
          <p>No members match the current filter.</p>
        </div>
      ) : (
        <div className="m-mem-list">
          {members.map(m => {
            const teams       = state.teams.filter(t => t.players?.includes(m.id));
            const cricketTeams = teams.filter(t => t.sport === "Cricket");
            const profileTeam  = !isAllTeams && activeTeam?.sport === "Cricket"
              ? activeTeam
              : cricketTeams[0];
            const primaryTeam = !isAllTeams && activeTeam
              ? activeTeam
              : (cricketTeams[0] || teams[0] || null);
            return (
              <MemberCard
                key={m.id}
                member={m}
                teams={teams}
                primaryTeam={primaryTeam}
                canUpdate={canUpdate}
                canDelete={canDelete}
                showStats={!!profileTeam}
                onStats={profileTeam ? () => setProfileFor({ user: m, team: profileTeam }) : undefined}
                onEdit={() => openEdit(m)}
                onRemove={() => remove(m)}
              />
            );
          })}
        </div>
      )}

      {profileFor && (
        <PlayerProfile
          user={profileFor.user}
          team={profileFor.team}
          currentUser={user}
          onClose={() => setProfileFor(null)}
        />
      )}
    </div>
  );
}
